import { types as cursorTypes } from './enums/cursor';
import { types as eventTypes } from './enums/event';
import subpixel from './util/canvasSubpixelOffset';
import {
	addCrosshairFlags,
	buildCrosshairData,
	concealCrosshairFlags,
	removeCrosshairFlags,
	renderCrosshairFlags,
	revealCrosshairFlags
} from './util/crosshair';

/** Class used to render/reveal/conceal crosshairs on panels */
export default class Crosshair {
	/**
	 * @param {Object} core - Modcharts chart object
	 * @param {Object} settings - Parameters to configure crosshair
	 */
	constructor(core, settings = {}) {
		/** @type {Object} */
		this.core = core;
		// Replace existing crosshair if necessary
		if (core.crosshair && core.crosshair.constructor.name === 'Crosshair') {
			// Destroy existing crosshair
			core.crosshair.destroy();
		}
		// Save crosshair to chart
		core.crosshair = this;

		/** @type {Boolean} */
		this.isNearEdge = false;
		/**
		 * @type {Object}
		 * @property {Boolean} params.enabled - Parameter to enable display of the crosshair
		 * @property {Boolean} params.flagEnabled - Parameter to enable display of the crosshair flags on the X and Y axes
		 * @property {Boolean} params.persist - Parameter to continue to display the crosshair after the mouse leaves the chart
		 * @property {Boolean} params.primaryOnly - Parameter to restrict display of the crosshair to the primary panel only
		 * @property {Boolean} params.returnClosestCustomData - Parameter to return data point closest to crosshair position or data point at exact crosshair position. If no data point is found, null will be returned for the data and y coordinate values.
		 * @property {Boolean} params.showCircle - Parameter to enable display of the crosshair circle on the chart data point under the mouse
		 * @property {Boolean} params.showFlagCrosshairDisabled - Parameter to display crosshair flag even if crosshair is disabled (still follows `flagEnabled` param)
		 * @property {Boolean} params.snapHorizontalToData - Parameter to position the horizontal crosshair line at the y position of the current data point (disabled is under mouse)
		 */
		this.params = { ...settings };

		this.listenerEvents = [
			eventTypes.CURSOR_CHANGE,
			eventTypes.DATA_LOAD_START,
			eventTypes.MOUSE_DOWN,
			eventTypes.MOUSE_LEAVE,
			eventTypes.MOUSE_MOVE,
			eventTypes.PANEL_MOUSE_MOVE,
			eventTypes.RESIZE,
			eventTypes.RESIZE_HANDLE_HOVER,
			eventTypes.TOOL_MOUSE_MOVE,
			eventTypes.ZOOM
		];

		this._eventHandlers = {
			[eventTypes.CURSOR_CHANGE]: ({ cursor }) => { this._enforceChartCursor(cursor); },
			[eventTypes.DATA_LOAD_START]: () => { this._concealCrosshairs(); },
			[eventTypes.MOUSE_DOWN]: () => { if (!this.core.state.isTouchDevice) this._concealCrosshairs(); },
			[eventTypes.MOUSE_LEAVE]: () => { if (!this._getParam('persist')) this._concealCrosshairs(); },
			[eventTypes.MOUSE_MOVE]: () => {
				if (core.state.zooming || core.dataXHR || core.status === 0) {
					this._concealCrosshairs();
				}
			},
			[eventTypes.PANEL_MOUSE_MOVE]: ({ mouse, activePanel }) => {
				if (this.core.state.updateCrosshairTimeout) {
					clearTimeout(this.core.state.updateCrosshairTimeout);
				}

				this.core.state.updateCrosshairTimeout = setTimeout(() => {
					this._updateCrosshair(mouse, activePanel);
					this._revealCrosshairs(activePanel);
				}, 0);

			},
			[eventTypes.RESIZE]: () => this._concealCrosshairs(),
			[eventTypes.RESIZE_HANDLE_HOVER]: () => this._concealCrosshairs(),
			[eventTypes.TOOL_MOUSE_MOVE]: () => this._concealCrosshairs(),
			[eventTypes.ZOOM]: () => this._concealCrosshairs()
		};

		// Register listeners
		this.listenerEvents.forEach(e => core.eventEmitter.on(e, this._eventHandlers[e]));
	}

	/**
	 * Returns the param value from the proper collection: the crosshair params if the param
	 * exists there, or the chart params.
	 * @param {string} name - The name of the param to retrieve
	 * @returns {*} The param value
	 */
	_getParam(name) {
		if (Object.prototype.hasOwnProperty.call(this.params, name)) return this.params[name];
		return this.core.params.crosshair[name];
	}

	/** Completely remove the crosshair from the chart */
	destroy() {
		const { core } = this;

		// Unregister listeners
		this.listenerEvents.forEach(e => core.eventEmitter.off(e, this._eventHandlers[e]));

		for (const panel of this.core.panels) {
			// Remove flags
			removeCrosshairFlags(panel);

			// Remove SVG elements
			['crosshairCircle', 'crosshairHoriz', 'crosshairVert'].forEach(id => {
				if (panel[id]) {
					panel[id].remove();
					delete panel[id];
				}
			});
		}

		// Kill reference from core to this crosshair
		core.crosshair = null;
	}

	/**
	 * Create crosshairs if they don't exist, update crosshair information and call render methods
	 * @param {Object} mouse - Mouse position information
	 * @param {Object} activePanel - Panel that is active (mouse is currently hovering over)
	 */
	_updateCrosshair(mouse, activePanel) {
		if (this.core.status === 0) return;
		if (!activePanel || !this.core.exchangeDates) return;

		// Update cursor when crosshair is enabled
		if (this._getParam('enabled')) {
			this.core.setCursor(cursorTypes.CROSSHAIR);
		} else {
			this.core.setCursor(cursorTypes.DEFAULT);
		}

		const [valX, valY] = mouse;
		const valIdx = this.core.panels[0].xAxis.primaryScale.invert(valX);
		const leftLimit = activePanel.size.padding.left;
		const rightLimit = activePanel.size.width + activePanel.size.padding.left;
		let indicator;
		let valDate;
		let valueY;
		let newX;
		let newY;
		let dataY;
		let dataIndex;
		const dataSource = this.core.params.customData ? this.core.dataPrimary : this.core.dataFrame;

		if (
			this.core.data &&
			this.core.state.toolmode === null &&
			dataSource
		) {
			dataIndex = this.core._getIndexByDomain(valIdx, dataSource, 'dateIndex');

			// Don't select index that is off of the chart
			const [domainLowerBound, domainUpperBound] = activePanel.xAxis.primaryScale.domain();
			const { dateIndex } = dataSource[dataIndex];
			if (dateIndex > domainUpperBound) dataIndex--;
			else if (dateIndex < domainLowerBound) dataIndex++;

			// fix errant errors while chart is changing state
			if (!dataSource[dataIndex]) return;

			newX = subpixel.px(
				Number(
					this.core.panels[0].xAxis.primaryScale(dataSource[dataIndex].dateIndex)
				)
			);

			if (isNaN(newX)) return;

			for (const panel of this.core.panels) {
				// Create the horizontal crosshair (this gets created on every panel)
				if (!panel.crosshairHoriz) {
					panel.crosshairHoriz = panel.rootOverlay
						.append('g')
						.attr('class', 'modcharts-crosshair-horiz')
						.append('line')
						.style('visibility', 'hidden');
				}

				// Create the vertical crosshair (this gets created on every panel)
				if (!panel.crosshairVert) {
					panel.crosshairVert = panel.rootOverlay
						.append('g')
						.attr('class', 'modcharts-crosshair-vert')
						.append('line')
						.style('visibility', 'hidden');
				}

				// Create the flag and circle on the upper panel only
				addCrosshairFlags(panel);

				// Create the circle on the upper panel only
				if (panel.isUpper && !panel.crosshairCircle) {
					panel.crosshairCircle = panel.rootOverlay
						.append('circle')
						.attr('class', 'modcharts-crosshair-circle')
						.attr('r', panel.params.style.crosshair.circleRadius)
						.style('visibility', 'hidden');
				}

				indicator = panel.indicators[0];

				// indicators can sometimes be removed before mousemove handler is complete
				if (!indicator) break;

				// vertical crosshair and data callback
				if (
					this.core.exchangeDates[Math.round(valIdx)] &&
					valX < rightLimit &&
					valX > leftLimit
				) {
					// Emit crosshair data for active panel
					if (panel === activePanel) {
						newY = subpixel.px(valY);
						valueY = this.core.params.customData
							? dataSource[dataIndex].value
							: dataSource[dataIndex].close;

						if (
							typeof valueY === 'number' &&
							this._getParam('enabled') &&
							this.core.data
						) {
							valDate = new Date(dataSource[dataIndex].date);
							dataY = Number(this.core.panels[0].yAxis.primaryScale(valueY));
							this._emitCrosshairData(activePanel, valDate, newX, newY, dataY);
						}
					}

					// Vertical crosshair is rendered on every panel
					this._renderCrosshairVertical(panel, newX, valDate);
				} else {
					// hide invalid crosshairs
					panel.crosshairVert.style('visibility', 'hidden');
				}
			}

			// Horizontal crosshair elements are only rendered on the active panel
			this._renderCrosshairHorizontal(activePanel, mouse, newX, newY, dataY);
		}
	}

	_emitCrosshairData(panel, valDate, newX, newY, dataY) {
		// Use setTimeout so as not to block when there are repeated calls to update the crosshair
		if (this.core.state.crosshairDataTimeout) {
			clearTimeout(this.core.state.crosshairDataTimeout);
		}
		this.core.state.crosshairDataTimeout = setTimeout(
			() => {
				this.core.eventEmitter.emit(
					eventTypes.CROSSHAIR_MOVE,
					buildCrosshairData(panel, valDate, newX, newY, dataY)
				);
			},
			0
		);
	}

	/**
	 * Render the vertical crosshair for a given panel
	 * @param {Object} panel - Panel on which to render crosshair
	 * @param {Number} xDate - Date for chart data point X coordinate closest to mouse X coordinate
	 * @param {Number} xValue - Chart data point X coordinate closest to mouse X coordinate
	 */
	_renderCrosshairVertical(panel, xValue, xDate) {
		// Render xAxis flag
		renderCrosshairFlags(panel, { xAxis: true, xDate, xValue });

		// primary panel crosshair only (NXC-657)
		if (!panel.isUpper && this._getParam('primaryOnly')) return;

		// Update crosshair vertical
		panel.crosshairVert
			.attr('x1', xValue)
			.attr('y1', panel.size.padding.top)
			.attr('x2', xValue)
			.attr('y2', panel.size.padding.top + panel.size.height)
			.style('visibility', 'hidden');

		// reflow
		try {
			panel.crosshairVert.node().getBBox();
		} catch (error) {
			this.warn(error.message);
		}
	}

	/**
	 * Render the horizontal crosshair for a given panel
	 * @param {Object} activePanel - Panel that is active (mouse is currently hovering over)
	 * @param {Number[]} mouse - Mouse position information [x, y]
	 * @param {Number} newX - Chart data point X coordinate closest to mouse X coordinate
	 * @param {Number} newY - Distance from top of active panel to Mouse Y coordinate
	 * @param {Number} dataY - Vertical location of the current chart data point under the crosshair
	 */
	_renderCrosshairHorizontal(
		activePanel,
		mouse,
		newX,
		newY,
		dataY
	) {
		if (isNaN(newY) || !activePanel) return;

		// Align upper panel horizontal crosshair to mouse Y (newY) or data Y (dataY) depending on preference
		if (
			this._getParam('snapHorizontalToData') &&
			activePanel.isUpper &&
			typeof dataY === 'number'
		) {
			newY = dataY;
		} else {
			// Adjust Y coord to be relative to active panel
			newY -= activePanel.size.top;
		}

		// hide flag/horizontal crosshair if the mouse nears vertical edge of the panel
		if (
			newY - 6 < activePanel.size.padding.top ||
			newY + 6 > activePanel.size.padding.top + activePanel.size.height
		) {
			this.isNearEdge = true;
		} else {
			this.isNearEdge = false;
		}

		// Update horizontal crosshair
		activePanel.crosshairHoriz
			.attr('x1', activePanel.size.padding.left)
			.attr('y1', newY)
			.attr('x2',	activePanel.size.padding.left + activePanel.size.width)
			.attr('y2', newY)
			.style('visibility', 'hidden');

		// Render yAxis flag
		renderCrosshairFlags(activePanel, { yAxis: true, yValue: newY });

		// Locate the crosshair circle on the chart
		if (
			activePanel.crosshairCircle &&
			!isNaN(dataY) &&
			typeof dataY === 'number' &&
			activePanel.isWithin(mouse)
		) {
			activePanel.crosshairCircle
				.attr('cx', newX)
				.attr('cy', dataY)
				.style('visibility', 'hidden');
		}

	}

	/** Conceal crosshairs on all panels. This is done using the 'visibility' style attribute. */
	_concealCrosshairs() {
		this.core.panels.forEach(panel => {
			// conceal horiz line
			if (panel.crosshairHoriz) {
				panel.crosshairHoriz.style('visibility', 'hidden');
			}

			// conceal vert line
			if (panel.crosshairVert) {
				panel.crosshairVert.style('visibility', 'hidden');
			}

			// Conceal crosshair flags
			concealCrosshairFlags(panel);

			// conceal circle (only will exist on first panel)
			if (panel.crosshairCircle) {
				panel.crosshairCircle.style('visibility', 'hidden');
			}
		});
		this.core.eventEmitter.emit(eventTypes.CROSSHAIR_HIDE);
	}

	/**
	 * Reveal vertical crosshairs on all panels; horizontal crosshair, flag, and circle on active panel
	 * @param {Object} activePanel - Panel that is active (mouse is currently hovering over)
	 */
	_revealCrosshairs(activePanel) {
		for (const panel of this.core.panels) {
			// Don't reveal crosshair if panel is lower and primary only option is true (NXC-657)
			if (!panel.isUpper &&	this._getParam('primaryOnly')) break;

			// Reveal vertical crosshair for panel
			if (
				this._getParam('enabled') &&
				panel.params.enableCrosshair &&
				panel.crosshairVert
			) {
				panel.crosshairVert.style('visibility', null);
			}

			// Reveal horizontal line, flag, circle for active panel only (flag, circle will appear on first panel only)
			if (panel === activePanel) {
				// Only show horizontal, flag if not near edge
				if (!this.isNearEdge) {
					if (
						this._getParam('enabled') &&
						panel.params.enableCrosshair &&
						panel.crosshairHoriz
					) {
						panel.crosshairHoriz.style('visibility', null);
					}

					if (
						(
							(this._getParam('enabled') && panel.params.enableCrosshair) ||
							// Check if flag should be shown even if crosshair is disabled
							this._getParam('showFlagCrosshairDisabled')
						) &&
						this._getParam('flagEnabled')
					) {
						revealCrosshairFlags(panel);
					}
				}

				if (
					this._getParam('enabled') &&
					panel.params.enableCrosshair &&
					this._getParam('showCircle') &&
					panel.crosshairCircle
				) {
					panel.crosshairCircle.style('visibility', null);
				}
			}
		}

		this.core.eventEmitter.emit(eventTypes.CROSSHAIR_SHOW);
	}

	/** Ensures chart does not use crosshair cursor when disabled */
	_enforceChartCursor(cursor) {
		if (cursor === cursorTypes.CROSSHAIR && !this._getParam('enabled')) {
			this.core.setCursor(cursorTypes.DEFAULT);
		}
	}

	/**
	 * Method used to change crosshair parameters
	 * @param {String} param - Name of parameter to change
	 * @param {Boolean} isEnabled - Turn parameter on/off
	 */
	setParam(param, isEnabled) {
		isEnabled = typeof isEnabled === 'boolean' ? isEnabled : true;

		// Save new value of param. Always save in chart params; only save in crosshair params if that param already exists.
		this.core.params.crosshair[param] = isEnabled;
		if (Object.prototype.hasOwnProperty.call(this.params, param)) {
			this.params[param] = isEnabled;
		}

		if (param === 'showCircle') {
			this.core.panels.forEach(panel => {
				if (panel.crosshairCircle) {
					let visibility = 'hidden';

					// Show circle if check passes
					if (this._getParam('showCircle')) visibility = null;

					panel.crosshairCircle.style('visibility', visibility);
				}
			});
		}
	}
}
